import type { Category } from '@scayle/storefront-nuxt'
import {
  linkCategoryTo,
  isHiddenForMainNavigation,
  isSaleCategory,
  findCategoryById,
} from '../utils/category'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import {
  isNavigationItemActive,
  resolveStoryblokLink,
} from '../utils/storyblok'
import type { NavigationItem } from '../types/navigation'

export function useNavigationMapping() {
  const resolveNavigationPath = (
    item: SbMainNavigation,
    scayleCategory?: Category,
  ): string => {
    return resolveStoryblokLink({
      link: item.link,
      category: scayleCategory,
      defaultPath: '/',
    })
  }

  const getItemIdentifier = (item: { id: string | number; name: string }) => {
    return typeof item.id === 'number' ? item.id : item.name.toLowerCase()
  }

  const getNavigationItems = (
    storyblokItems: SbMainNavigation[],
    scayleCategories: Category[],
  ): NavigationItem[] => {
    const activeItems = storyblokItems.filter(isNavigationItemActive)

    const mappedItems = activeItems
      .map((item) => {
        // Case 1: Has RootCategoryID - enrich with Scayle category data.
        // The id may reference any category in the tree, not only a root one,
        // so we search recursively through children.
        if (item.RootCategoryID) {
          const scayleCategory = findCategoryById(
            scayleCategories,
            item.RootCategoryID,
          )
          if (scayleCategory && !isHiddenForMainNavigation(scayleCategory)) {
            return createNavigationItem(item, scayleCategory)
          }
        }

        // Case 2: Try to match with Scayle category by name or path
        const matchingCategory = findMatchingCategory(item, scayleCategories)
        if (matchingCategory) {
          return createNavigationItem(item, matchingCategory)
        }

        // Case 3: Special case for Sale - try to find sale category if no explicit match
        if (item.label?.toLowerCase() === 'sale') {
          const saleCategory = scayleCategories.find((cat) =>
            isSaleCategory(cat),
          )
          if (saleCategory) {
            return createNavigationItem(item, saleCategory)
          }
        }

        // Case 4: No RootCategoryID - create independent navigation item
        // Any MainNavigation item without a RootCategoryID should display independently
        if (!item.RootCategoryID) {
          return createIndependentNavigationItem(item)
        }

        return null
      })
      .filter((item): item is NavigationItem => item !== null)

    const usedCategoryIds = new Set(
      mappedItems.map((item) => getItemIdentifier(item)),
    )

    // Helper to check if a category has any matching Storyblok item (active or not)
    const hasStoryblokMatch = (category: Category) => {
      return storyblokItems.some(
        (item) =>
          item.RootCategoryID === category.id.toString() ||
          (item.label &&
            category.name &&
            item.label.toLowerCase() === category.name.toLowerCase()) ||
          (item.link?.cached_url &&
            category.path &&
            category.path.includes(item.link.cached_url.replace(/^\//, ''))),
      )
    }

    const remainingScayleCategories = scayleCategories
      .filter((category) => {
        // Skip if category is hidden or is a sale category
        if (isHiddenForMainNavigation(category) || isSaleCategory(category)) {
          return false
        }
        // Skip if category ID or name is already used
        if (
          usedCategoryIds.has(category.id) ||
          usedCategoryIds.has(category.name?.toLowerCase() || '')
        ) {
          return false
        }
        // Skip if category has any matching Storyblok item (even inactive ones)
        return !hasStoryblokMatch(category)
      })
      .map(
        (category) =>
          ({
            id: category.id,
            name: category.name || '',
            path: linkCategoryTo(category),
            NavigationItem: [],
            NavigationFlyoutContent: [],
            ClickableImage: [],
            children: filterHiddenForMainNavigation(category.children || []),
            isSale: isSaleCategory(category),
          }) as NavigationItem,
      )

    return [...mappedItems, ...remainingScayleCategories]
  }

  function filterHiddenForMainNavigation(
    categories: Category[] = [],
  ): Category[] {
    return categories.reduce((acc, cat) => {
      if (!isHiddenForMainNavigation(cat)) {
        acc.push({
          ...cat,
          children: filterHiddenForMainNavigation(cat.children || []),
        })
      }
      return acc
    }, [] as Category[])
  }

  function createNavigationItem(
    item: SbMainNavigation,
    category: Category,
  ): NavigationItem {
    return {
      id: category.id,
      name: item.label || category.name || '',
      path: resolveNavigationPath(item, category),
      NavigationItem: item.NavigationItem || [],
      NavigationFlyoutContent: item.NavigationFlyoutContent || [],
      ClickableImage: item.ClickableImage || [],
      children: filterHiddenForMainNavigation(category.children) || [],
      isSale: isSaleCategory(category),
    }
  }

  function createIndependentNavigationItem(
    item: SbMainNavigation,
  ): NavigationItem {
    // For items without a link, resolve to a default path (usually home page)
    const path = item.link?.cached_url ? resolveNavigationPath(item) : '/'

    return {
      id: item._uid,
      name: item.label || '',
      path,
      NavigationItem: item.NavigationItem || [],
      NavigationFlyoutContent: item.NavigationFlyoutContent || [],
      ClickableImage: item.ClickableImage || [],
      children: [],
      isSale: false,
    }
  }

  function findMatchingCategory(
    item: SbMainNavigation,
    categories: Category[],
  ): Category | undefined {
    return categories.find((cat) => {
      if (isHiddenForMainNavigation(cat)) return false

      if (item.label && cat.name?.toLowerCase() === item.label.toLowerCase()) {
        return true
      }

      if (item.link?.cached_url) {
        const categoryPath = linkCategoryTo(cat).toString()
        const storyblokPath = item.link.cached_url.replace(/^\//, '')
        return categoryPath.endsWith(storyblokPath)
      }

      return false
    })
  }

  return {
    getNavigationItems,
  }
}
